import {
	HttpEvent,
	HttpHandlerFn,
	HttpInterceptorFn,
	HttpRequest,
	HttpResponse,
	HttpStatusCode,
} from '@angular/common/http'
import { inject } from '@angular/core'
import { Router } from '@angular/router'
import { AbstractNotification } from '@mos/api/notification/dto-client'
import { AppConfigService } from '@mos/client/shared/app-config'
import { CookieService } from 'ngx-cookie-service'
import { catchError, map, Observable, retry, timer } from 'rxjs'
import { AcquisitionRepository } from './acquisition.repository'
import { AuthRepository } from './auth.repository'

const getHeaders = (
	request: HttpRequest<unknown>,
	appConfigService: AppConfigService,
	cookieService: CookieService,
) => {
	// X-APP-LANG suit toujours la langue générale de l'admin (sélecteur FR/EN du menu, piloté par
	// NavbarRepository/AppConfigService.locale) - y compris pour un traducteur : c'est ce même sélecteur
	// qui lui sert à choisir quelle langue il voit/édite (voir TranslatorAccessService.canEditI18nFields,
	// qui compare cette même langue à ses langues assignées côté formulaires, et
	// restrictDtoToTranslatorScope qui l'applique côté serveur).
	let headers = request.headers
		.set('X-APP-VERSION', appConfigService.getEnv().version)
		.set('X-MOS-SUPPORT', appConfigService.getSupportedCompressionTypes().join(','))
		.set('X-APP-CONTEXT', appConfigService.getClientApp())
		.set('X-OS-VERSION', appConfigService.getEnv().osVersion ?? '')
		.set('X-APP-LANG', appConfigService.getLocale())

	// tracking meta
	if (cookieService.check('_fbp')) {
		headers = headers.set('X-FBP', cookieService.get('_fbp'))
	}
	if (cookieService.check('_fbc')) {
		headers = headers.set('X-FBC', cookieService.get('_fbc'))
	}

	const acquisition = inject(AcquisitionRepository).data
	if (acquisition) {
		headers = headers.set('X-Acquisition', encodeURIComponent(JSON.stringify(acquisition)))
	}

	const token = inject(AuthRepository).state.token
	return token ? headers.set('Authorization', `Bearer ${token}`) : headers
}

export interface MosResponse<T> {
	data?: T
	metadata?: { notifications: AbstractNotification[] }
}

export const apiInterceptor: HttpInterceptorFn = (
	req: HttpRequest<unknown>,
	next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
	const appConfigService = inject(AppConfigService)
	const authRepository = inject(AuthRepository)
	const router = inject(Router)
	const cookieService = inject(CookieService)

	if (req.url.indexOf('@api') === -1) {
		return next(req)
	}

	const newRequest = req.clone({
		url: req.url.replace('@api', appConfigService.getApiUrl()),
		headers: getHeaders(req, appConfigService, cookieService),
	})

	return next(newRequest).pipe(
		retry({
			count: 2,
			delay: (error, retryCount) => {
				// Only retry idempotent GET requests that never reached the server (status 0 -
				// network failure, e.g. "Load failed"). Never retry a request that got a real
				// response, and never retry non-idempotent methods (POST/PUT/DELETE).
				if (newRequest.method !== 'GET' || error?.status !== 0) {
					throw error
				}
				return timer(300 * retryCount)
			},
		}),
		map((response) => {
			if (response instanceof HttpResponse) {
				return response.clone({ body: (response.body as MosResponse<unknown>).data })
			}
			return response
		}),
		catchError((error) => {
			if (authRepository.player !== null && error.status === HttpStatusCode.Unauthorized) {
				authRepository.removePlayer()
				router.navigateByUrl('/', { onSameUrlNavigation: 'reload' }).then()
			}
			throw error
		}),
	)
}
